// client-v2/src/api/index.ts
import type { AppType } from "@nextgammon/server";
import { hc } from "hono/client";
import { MaintenanceError } from "./errors";

// API URL configuration
const API_URL = process.env.BUN_PUBLIC_API_URL;

/**
 * Custom fetch wrapper that handles maintenance mode responses
 */
const fetchWithMaintenanceHandling = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  try {
    const response = await fetch(input, init);

    // Check for maintenance mode
    if (response.status === 503) {
      try {
        const data = await response.clone().json();
        if (data.maintenance) {
          throw new MaintenanceError(data.message || "System under maintenance");
        }
      } catch (e) {
        // Re-throw if it's already a MaintenanceError
        if (e instanceof MaintenanceError) throw e;
        // Otherwise continue with normal error handling
      }
    }

    return response;
  } catch (error) {
    // Log the actual error for debugging
    console.error("Fetch error:", error);
    throw error;
  }
};

/**
 * Create the default unauthenticated client
 */
export const api = hc<AppType>(API_URL, { fetch: fetchWithMaintenanceHandling });

/**
 * Creates an authenticated API client with the authorization header
 */
export const authenticatedApi = (token: string) => {
  // Create a custom fetch function that adds the authorization header and handles maintenance
  const fetchWithAuth = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    // Create new headers with Authorization
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);

    // Use our maintenance-aware fetch
    return fetchWithMaintenanceHandling(input, { ...init, headers });
  };

  // Create a complete fetch implementation with auth
  const customFetch = Object.assign(fetchWithAuth, fetch) as typeof fetch;

  // Create a new client with our custom fetch
  return hc<AppType>(API_URL, { fetch: customFetch });
};
